import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
export const Route = createFileRoute("/user/_layout/crm/leads")({
  component: LeadsPage,
});

import { useState, useEffect, useMemo } from "react";
import instance from "@/lib/axios";
import { getCrmUrl } from "@/lib/crm";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { LeadTable } from "@/components/crm/tables/LeadTable";
import {
  LeadFormSheet,
  LEAD_STATUSES,
  LEAD_SOURCES,
} from "@/components/crm/forms/LeadFormSheet";
import { LeadConvertDialog } from "@/components/crm/shared/LeadConvertDialog";
import {
  useFetchLeads,
  useLeadMutations,
  useConvertLead,
  type LeadOrdering,
} from "@/hooks/api/crm/useLeads";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import type { CrmLead, CrmLeadPayload, LeadConvertPayload } from "@/types/crm";

function LeadsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  // Undefined = all team-visible records; pick a user to narrow.
  const [assignedTo, setAssignedTo] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        first_name: SortDirection;
        title: SortDirection;
        company: SortDirection;
        status: SortDirection;
        source: SortDirection;
        email: SortDirection;
        assigned_to__first_name: SortDirection;
      },
      LeadOrdering
    >({
      first_name: null,
      title: null,
      company: null,
      status: null,
      source: null,
      email: null,
      assigned_to__first_name: null,
    });

  const leadsQuery = useFetchLeads({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    source: sourceFilter || undefined,
    assigned_to: assignedTo ? String(assignedTo) : undefined,
    ordering: getApiOrdering(),
  });
  const leads = leadsQuery.data?.results ?? [];
  const count = leadsQuery.data?.count ?? 0;

  const { createLead, updateLead, deleteLead } = useLeadMutations();
  const convertLead = useConvertLead();

  const formMutating = createLead.isPending || updateLead.isPending;
  const isMutating =
    formMutating || deleteLead.isPending || convertLead.isPending;
  const isLoading = leadsQuery.isPending || isMutating;
  const isFetching = leadsQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((count ?? 0) / pageSize)),
    [count, pageSize]
  );

  const location = useLocation();
  const viewId = new URLSearchParams(location.searchStr).get("view") ?? undefined;
  const navigate = useNavigate();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<CrmLead | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<CrmLead | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState<CrmLead | null>(null);
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);

  useEffect(() => {
    if (!viewId) return;
    instance
      .get<CrmLead>(getCrmUrl(`leads/${viewId}/`))
      .then((res) => {
        setEditingLead(res.data);
        setIsViewOnly(true);
        setIsSheetOpen(true);
      })
      .catch(() =>
        navigate({ to: "/user/crm/leads", search: {} })
      );
  }, [viewId]);

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    if (viewId)
      navigate({ to: "/user/crm/leads", search: {} });
  };

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setStatusFilter("");
    setSourceFilter("");
    setAssignedTo(undefined);
    setOrdering({
      first_name: null,
      title: null,
      company: null,
      status: null,
      source: null,
      email: null,
      assigned_to__first_name: null,
    });
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, sourceFilter, assignedTo, ordering, setPage]);

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const onCreate = async (data: CrmLeadPayload) => {
    await createLead.mutateAsync(data);
    toast.success("Lead created successfully!");
    setIsSheetOpen(false);
  };

  const onEdit = async (id: string, data: Partial<CrmLeadPayload>) => {
    await updateLead.mutateAsync({ id, payload: data });
    toast.success("Lead updated successfully!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!leadToDelete) return;
    try {
      await deleteLead.mutateAsync({ id: leadToDelete.id });
      toast.success("Lead deleted successfully!");
      setIsDeleteDialogOpen(false);
      setLeadToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleConvertConfirm = async (payload: LeadConvertPayload) => {
    if (!leadToConvert) return;
    await convertLead.mutateAsync({ id: leadToConvert.id, payload });
    toast.success(
      "Lead converted successfully! Account, Contact, and Opportunity created."
    );
    setIsConvertDialogOpen(false);
    setLeadToConvert(null);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Leads"
          description="Manage your CRM leads."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => {
            setEditingLead(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Lead"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search leads..."
          />
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {LEAD_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={sourceFilter}
            onValueChange={(v) => setSourceFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {LEAD_SOURCES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} />
        </FiltersPanel>

        <LeadTable
          leads={leads}
          onView={(l) => {
            navigate({
              to: "/user/crm/leads/$id",
              params: { id: String(l.id) },
            });
          }}
          onEdit={(l) => {
            setEditingLead(l);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(l) => {
            setLeadToDelete(l);
            setIsDeleteDialogOpen(true);
          }}
          onConvert={(l) => {
            setLeadToConvert(l);
            setIsConvertDialogOpen(true);
          }}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <LeadFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={handleCloseSheet}
          onCreate={onCreate}
          onEdit={onEdit}
          editingLead={editingLead}
          isLoading={formMutating}
        />

        <LeadConvertDialog
          isOpen={isConvertDialogOpen}
          onClose={() => {
            setIsConvertDialogOpen(false);
            setLeadToConvert(null);
          }}
          onConfirm={handleConvertConfirm}
          lead={leadToConvert}
          isLoading={convertLead.isPending}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Lead"
          description={`Are you sure you want to delete "${leadToDelete?.first_name} ${leadToDelete?.last_name}"?`}
          itemName={`${leadToDelete?.first_name} ${leadToDelete?.last_name}`}
          isLoading={deleteLead.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
